// src/components/JurnalPembelajaran.jsx
import { useState, useEffect, useMemo, useCallback } from 'react';
import XLSX from 'xlsx-js-style';
import { api } from '../services/api';

const PRIORITAS_HARIAN = ['Alpha', 'Sakit', 'Izin', 'Terlambat'];

function formatTanggalPanjang(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const hari = d.toLocaleDateString('id-ID', { weekday: 'long' });
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${hari}, ${dd}-${mm}-${yyyy}`;
}

function tahunPelajaranDefault() {
  const now = new Date();
  const y = now.getFullYear();
  // Tahun pelajaran baru dianggap mulai Juli
  return now.getMonth() + 1 >= 7 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
}

function faseDariKelas(kelas) {
  const k = String(kelas || '').trim().toUpperCase();
  if (k.startsWith('XI') || k.startsWith('XII')) return 'F';
  if (k.startsWith('X')) return 'E';
  return '';
}

// Meringkas status kehadiran 1 hari dari banyak baris (banyak siswa x banyak jam)
// jadi 1 kalimat, mis. "Hadir 27 siswa" atau "Hadir 24, Alpha 2, Sakit 1".
function ringkasanKehadiran(recordsHariItu, rosterSize) {
  // Siswa pindahan yang belum resmi terdaftar saat itu ("Tidak Berlaku") dikecualikan total.
  const records = recordsHariItu.filter((r) => r.Status !== 'Tidak Berlaku');
  if (records.length === 0) return 'Menyusul';
  const perSiswa = {};
  records.forEach((r) => {
    if (!perSiswa[r.ID_Siswa]) perSiswa[r.ID_Siswa] = [];
    perSiswa[r.ID_Siswa].push(r.Status);
  });
  let hadir = 0, alpha = 0, sakit = 0, izin = 0, terlambat = 0;
  Object.values(perSiswa).forEach((statuses) => {
    const berat = PRIORITAS_HARIAN.find((p) => statuses.includes(p)) || 'Hadir';
    if (berat === 'Hadir') hadir++;
    else if (berat === 'Alpha') alpha++;
    else if (berat === 'Sakit') sakit++;
    else if (berat === 'Izin') izin++;
    else if (berat === 'Terlambat') terlambat++;
  });
  if (rosterSize > 0 && hadir === rosterSize) return `Hadir ${hadir} siswa`;
  const bagian = [`Hadir ${hadir}`];
  if (alpha) bagian.push(`Alpha ${alpha}`);
  if (sakit) bagian.push(`Sakit ${sakit}`);
  if (izin) bagian.push(`Izin ${izin}`);
  if (terlambat) bagian.push(`Terlambat ${terlambat}`);
  return bagian.join(', ');
}

export default function JurnalPembelajaran({ user, onBack }) {
  const [siswaAll, setSiswaAll] = useState([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [kelas, setKelas] = useState('');
  const [mataPelajaran, setMataPelajaran] = useState('Sosiologi');
  const [namaGuru, setNamaGuru] = useState(user?.nama || '');
  const [tahunPelajaran, setTahunPelajaran] = useState(tahunPelajaranDefault());
  const [semester, setSemester] = useState('Ganjil');

  const [rows, setRows] = useState([]); // [{tanggal, tp, materi, catatan, mataPelajaran, savedTp, savedMateri, savedCatatan}]
  const [loading, setLoading] = useState(false);
  const [savingTanggal, setSavingTanggal] = useState(null);

  const [tambahTanggal, setTambahTanggal] = useState('');
  const [showTambah, setShowTambah] = useState(false);
  const [selectedTanggal, setSelectedTanggal] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getSiswa('Semua Kelas');
        setSiswaAll((res.data || []).filter((s) => s.Status === 'Aktif'));
      } catch (err) {
        setError(err.message);
      }
    })();
  }, []);

  const kelasList = useMemo(
    () => Array.from(new Set(siswaAll.map((s) => s.Kelas).filter(Boolean))).sort(),
    [siswaAll]
  );

  const rosterSize = useMemo(() => siswaAll.filter((s) => s.Kelas === kelas).length, [siswaAll, kelas]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast((prev) => (prev && prev.message === message ? null : prev)), 3500);
  }, []);

  const loadData = useCallback(async (kelasNama) => {
    if (!kelasNama) { setRows([]); return; }
    setLoading(true);
    setError('');
    try {
      const [resKehadiran, resJurnal] = await Promise.all([
        api.getKehadiranByKelas(kelasNama),
        api.getJurnal(kelasNama),
      ]);
      const kehadiran = resKehadiran.data || [];
      const jurnal = resJurnal.data || [];

      const tanggalDariAbsen = Array.from(new Set(kehadiran.map((r) => r.Tanggal)));
      const tanggalDariJurnal = Array.from(new Set(jurnal.map((r) => r.Tanggal)));
      const semuaTanggal = Array.from(new Set([...tanggalDariAbsen, ...tanggalDariJurnal])).sort();

      const built = semuaTanggal.map((t) => {
        const recsHariItu = kehadiran.filter((r) => r.Tanggal === t);
        const jEntry = jurnal.find((j) => j.Tanggal === t);
        return {
          tanggal: t,
          tp: jEntry?.TujuanPembelajaran || '',
          materi: jEntry?.Materi || '',
          catatan: jEntry?.Catatan || '',
          mataPelajaran: jEntry?.MataPelajaran || recsHariItu[0]?.MataPelajaran || '',
          kehadiranText: ringkasanKehadiran(recsHariItu, siswaAll.filter((s) => s.Kelas === kelasNama).length),
          adaAbsen: recsHariItu.length > 0,
        };
      });
      setRows(built);

      // auto-isi Mata Pelajaran dari data terakhir kalau field masih kosong/default
      const mapelTerdeteksi = built.find((r) => r.mataPelajaran)?.mataPelajaran;
      if (mapelTerdeteksi) setMataPelajaran(mapelTerdeteksi);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [siswaAll]);

  useEffect(() => { loadData(kelas); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kelas]);

  function updateRow(tanggal, field, value) {
    setRows((prev) => prev.map((r) => (r.tanggal === tanggal ? { ...r, [field]: value } : r)));
  }

  async function handleSimpanBaris(row) {
    setSavingTanggal(row.tanggal);
    setError('');
    try {
      await api.saveJurnalEntry({
        kelas,
        tanggal: row.tanggal,
        mataPelajaran: row.mataPelajaran || mataPelajaran,
        tujuanPembelajaran: row.tp,
        materi: row.materi,
        catatan: row.catatan,
      });
      showToast('success', `Jurnal ${formatTanggalPanjang(row.tanggal)} tersimpan.`);
    } catch (err) {
      showToast('error', `Gagal menyimpan: ${err.message}`);
      setError(err.message);
    } finally {
      setSavingTanggal(null);
    }
  }

  async function handleHapusBaris(row) {
    if (row.adaAbsen) return; // baris dengan data absen tidak boleh dihapus, hanya bisa dikosongkan isinya
    if (!window.confirm(`Hapus pertemuan tanggal ${formatTanggalPanjang(row.tanggal)} dari jurnal?`)) return;
    try {
      await api.hapusJurnalEntry(row.tanggal, kelas);
      setRows((prev) => prev.filter((r) => r.tanggal !== row.tanggal));
      showToast('success', 'Pertemuan dihapus dari jurnal.');
    } catch (err) {
      showToast('error', `Gagal menghapus: ${err.message}`);
    }
  }

  function handleTambahTanggal() {
    if (!tambahTanggal) { setError('Pilih tanggal dulu.'); return; }
    if (rows.some((r) => r.tanggal === tambahTanggal)) { setError('Tanggal itu sudah ada di jurnal.'); return; }
    setRows((prev) => [
      ...prev,
      { tanggal: tambahTanggal, tp: '', materi: '', catatan: '', mataPelajaran, kehadiranText: 'Menyusul', adaAbsen: false },
    ].sort((a, b) => (a.tanggal < b.tanggal ? -1 : 1)));
    setTambahTanggal('');
    setShowTambah(false);
    setError('');
  }

  const sortedRows = useMemo(() => [...rows].sort((a, b) => (a.tanggal < b.tanggal ? -1 : 1)), [rows]);

  // Dropdown tanggal pertemuan tidak menampilkan semua tanggal otomatis begitu saja —
  // guru pilih sendiri lewat dropdown. Default: pertemuan paling baru (kalau ada),
  // dan otomatis di-reset kalau tanggal yang lagi dipilih sudah tidak ada di daftar.
  useEffect(() => {
    if (sortedRows.length === 0) { setSelectedTanggal(''); return; }
    setSelectedTanggal((prev) => (sortedRows.some((r) => r.tanggal === prev) ? prev : sortedRows[sortedRows.length - 1].tanggal));
  }, [sortedRows]);

  const selectedIndex = sortedRows.findIndex((r) => r.tanggal === selectedTanggal);
  const selectedRow = selectedIndex >= 0 ? sortedRows[selectedIndex] : null;

  function exportJurnalExcel() {
    if (sortedRows.length === 0) {
      setError('Belum ada pertemuan untuk diekspor.');
      return;
    }
    const THIN = { style: 'thin', color: { rgb: 'A0A0A0' } };
    const BORDER_ALL = { top: THIN, bottom: THIN, left: THIN, right: THIN };
    const headerStyle = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      fill: { fgColor: { rgb: 'DCE6F7' } },
      border: BORDER_ALL,
    };
    const dataStyle = { alignment: { horizontal: 'left', vertical: 'top', wrapText: true }, border: BORDER_ALL, font: { sz: 10 } };
    const dataCenterStyle = { alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: BORDER_ALL, font: { sz: 10 } };
    const labelStyle = { font: { bold: true, sz: 11 } };
    const valueStyle = { font: { sz: 11 } };

    const aoa = [];
    aoa.push(['JURNAL MENGAJAR GURU']);
    aoa.push([`TAHUN PELAJARAN ${tahunPelajaran}`]);
    aoa.push(['UPT SMAN 3 Luwu Utara']);
    aoa.push([]);
    aoa.push(['Mata Pelajaran', `: ${mataPelajaran}`, '', 'Kelas / Semester', `: ${kelas} / ${semester}`, '']);
    aoa.push(['Nama Guru', `: ${namaGuru}`, '', 'Fase', `: ${faseDariKelas(kelas)}`, '']);
    aoa.push([]);
    const headerRowIdx = aoa.length;
    aoa.push(['HARI/\nTANGGAL', 'PERTEMUAN\nKE-', 'TUJUAN PEMBELAJARAN', 'MATERI', 'KEHADIRAN', 'CATATAN']);
    const dataStartIdx = aoa.length;

    sortedRows.forEach((r, i) => {
      aoa.push([formatTanggalPanjang(r.tanggal), i + 1, r.tp, r.materi, r.kehadiranText, r.catatan]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
      { s: { r: 4, c: 1 }, e: { r: 4, c: 2 } }, // value "Mata Pelajaran" — supaya nama panjang tidak kepotong
      { s: { r: 5, c: 1 }, e: { r: 5, c: 2 } }, // value "Nama Guru"
      { s: { r: 4, c: 4 }, e: { r: 4, c: 5 } }, // value "Kelas / Semester"
      { s: { r: 5, c: 4 }, e: { r: 5, c: 5 } }, // value "Fase"
    ];
    ws['!cols'] = [{ wch: 18 }, { wch: 13 }, { wch: 34 }, { wch: 30 }, { wch: 16 }, { wch: 30 }];

    // Tinggi baris data mengikuti panjang teks terpanjang di antara TP/Materi/Catatan,
    // supaya teks panjang tidak kepotong secara visual (perkiraan jumlah baris wrap).
    function tinggiBaris(text, lebarKolomKarakter) {
      if (!text) return 60;
      const jumlahBaris = Math.ceil(String(text).length / lebarKolomKarakter) || 1;
      return Math.max(60, jumlahBaris * 16 + 14);
    }

    ws['!rows'] = [
      { hpt: 20 }, { hpt: 16 }, { hpt: 16 }, { hpt: 10 },
      { hpt: 18 }, { hpt: 18 }, { hpt: 10 },
      { hpt: 30 },
      ...sortedRows.map((r) => ({
        hpt: Math.max(tinggiBaris(r.tp, 34), tinggiBaris(r.materi, 30), tinggiBaris(r.catatan, 30)),
      })),
    ];

    // styling judul & info
    [0, 1, 2].forEach((r) => { const a = XLSX.utils.encode_cell({ r, c: 0 }); if (ws[a]) ws[a].s = { font: { bold: true, sz: r === 0 ? 14 : 11 }, alignment: { horizontal: 'center' } }; });
    [4, 5].forEach((r) => {
      [0, 3].forEach((c) => { const a = XLSX.utils.encode_cell({ r, c }); if (ws[a]) ws[a].s = labelStyle; });
      [1, 4].forEach((c) => { const a = XLSX.utils.encode_cell({ r, c }); if (ws[a]) ws[a].s = { ...valueStyle, alignment: { wrapText: true, vertical: 'center' } }; });
    });

    // header tabel
    for (let c = 0; c <= 5; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRowIdx, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = headerStyle;
    }
    // data
    for (let r = dataStartIdx; r < dataStartIdx + sortedRows.length; r++) {
      for (let c = 0; c <= 5; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = (c === 1 || c === 4) ? dataCenterStyle : dataStyle;
      }
    }

    const wb = XLSX.utils.book_new();
    const sheetName = String(kelas).replace(/[\\/*?:[\]]/g, '-').slice(0, 31) || 'Jurnal';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `Jurnal_Mengajar_${mataPelajaran || 'Sosiologi'}_${kelas}.xlsx`);
  }

  const inputCls =
    'w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';
  const textareaCls =
    'w-full px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[70px]';

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-[90vw]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
            <span aria-hidden="true">{toast.type === 'success' ? '✓' : '⚠'}</span>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="border-l-4 border-blue-600 pl-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Jurnal Pembelajaran</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Kehadiran otomatis dari absen, Anda cukup isi TP/Materi/Catatan</p>
          </div>
          <button onClick={onBack} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium px-4 py-2 rounded-lg transition">
            &larr; Kembali ke Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

        {/* KOP JURNAL */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-blue-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128211;</span> Kop Jurnal &amp; Kelas
            </h2>
          </div>
          <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Kelas</label>
              <select value={kelas} onChange={(e) => setKelas(e.target.value)} className={inputCls}>
                <option value="">-- Pilih Kelas --</option>
                {kelasList.map((k) => <option key={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Mata Pelajaran</label>
              <input value={mataPelajaran} onChange={(e) => setMataPelajaran(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Semester</label>
              <select value={semester} onChange={(e) => setSemester(e.target.value)} className={inputCls}>
                <option>Ganjil</option>
                <option>Genap</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Nama Guru</label>
              <input value={namaGuru} onChange={(e) => setNamaGuru(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Tahun Pelajaran</label>
              <input value={tahunPelajaran} onChange={(e) => setTahunPelajaran(e.target.value)} className={inputCls} placeholder="2026/2027" />
            </div>
            <div>
              <label className={labelCls}>Fase (otomatis dari kelas)</label>
              <input value={faseDariKelas(kelas)} readOnly className={`${inputCls} bg-slate-50 text-slate-500`} />
            </div>
          </div>
        </section>

        {/* DAFTAR PERTEMUAN */}
        {kelas && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-indigo-600 px-5 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <span aria-hidden="true">&#128218;</span> Daftar Pertemuan ({sortedRows.length})
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowTambah((v) => !v)}
                  className="bg-white/90 hover:bg-white text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full transition"
                >
                  + Tambah Tanggal
                </button>
                <button
                  onClick={exportJurnalExcel}
                  className="bg-white text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full hover:bg-indigo-50 transition"
                >
                  Unduh Excel
                </button>
              </div>
            </div>

            {showTambah && (
              <div className="px-4 sm:px-6 py-3 bg-indigo-50/60 border-b border-slate-100 flex flex-wrap items-end gap-3">
                <div>
                  <label className={labelCls}>Tanggal pertemuan baru</label>
                  <input type="date" value={tambahTanggal} onChange={(e) => setTambahTanggal(e.target.value)} className={inputCls} />
                </div>
                <button onClick={handleTambahTanggal} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition">
                  Tambah
                </button>
                <p className="text-xs text-slate-400 max-w-xs">
                  Pakai ini kalau mau menulis jurnal untuk pertemuan yang absennya belum diinput (kehadiran otomatis "Menyusul").
                </p>
              </div>
            )}

            {loading ? (
              <p className="text-center text-slate-500 py-10">Memuat data...</p>
            ) : sortedRows.length === 0 ? (
              <p className="text-center text-slate-500 py-10">
                Belum ada pertemuan. Lakukan Catat Kehadiran dulu, atau klik "+ Tambah Tanggal" di atas.
              </p>
            ) : (
              <>
                {/* DROPDOWN PILIH TANGGAL PERTEMUAN — tidak menampilkan semua tanggal sekaligus,
                    guru pilih satu per satu supaya tidak bingung kalau tidak tiap hari mengajar kelas ini. */}
                <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
                  <label className={labelCls}>Pilih Tanggal Pertemuan</label>
                  <select value={selectedTanggal} onChange={(e) => setSelectedTanggal(e.target.value)} className={inputCls}>
                    <option value="">-- Pilih Tanggal Pertemuan --</option>
                    {sortedRows.map((r, i) => (
                      <option key={r.tanggal} value={r.tanggal}>
                        Pertemuan {i + 1} — {formatTanggalPanjang(r.tanggal)} ({r.kehadiranText}){r.tp ? ' ✓ sudah diisi' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedRow && (
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-3">
                        <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-full">
                          Pertemuan {selectedIndex + 1}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">{formatTanggalPanjang(selectedRow.tanggal)}</span>
                      </div>
                      <span
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                          selectedRow.kehadiranText === 'Menyusul' ? 'bg-slate-100 text-slate-500' : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {selectedRow.kehadiranText}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Tujuan Pembelajaran</label>
                        <textarea
                          value={selectedRow.tp}
                          onChange={(e) => updateRow(selectedRow.tanggal, 'tp', e.target.value)}
                          className={textareaCls}
                          placeholder="Peserta didik mampu..."
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Materi</label>
                        <textarea
                          value={selectedRow.materi}
                          onChange={(e) => updateRow(selectedRow.tanggal, 'materi', e.target.value)}
                          className={textareaCls}
                          placeholder="Pokok bahasan hari ini"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Catatan</label>
                        <textarea
                          value={selectedRow.catatan}
                          onChange={(e) => updateRow(selectedRow.tanggal, 'catatan', e.target.value)}
                          className={textareaCls}
                          placeholder="Kendala, refleksi, dll (opsional)"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      {!selectedRow.adaAbsen && (
                        <button
                          onClick={() => handleHapusBaris(selectedRow)}
                          className="text-xs font-medium text-red-600 hover:underline px-2"
                        >
                          Hapus Pertemuan
                        </button>
                      )}
                      <button
                        onClick={() => handleSimpanBaris(selectedRow)}
                        disabled={savingTanggal === selectedRow.tanggal}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-medium px-4 py-2 rounded-lg transition"
                      >
                        {savingTanggal === selectedRow.tanggal ? 'Menyimpan...' : 'Simpan Baris Ini'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
